import { useRef, useState, useCallback } from "react";
import { Play, Pause, Mic } from "lucide-react";

/** m:ss, guarding the webm `Infinity`/`NaN` duration case. */
function fmtDur(s: number): string {
  if (!isFinite(s) || s < 0 || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const WAVEFORM_BARS = 27;

/** Fixed pseudo-random bar heights — same trick as the WhatsApp module's
 *  AudioPlayer, so every voice note gets a natural-looking static waveform. */
const SEED_HEIGHTS = [
  0.3, 0.5, 0.8, 0.45, 0.9, 0.6, 0.35, 0.75, 0.5, 0.65, 0.85, 0.4, 0.7, 0.55,
  0.42, 0.82, 0.6, 0.38, 0.72, 0.48, 0.68, 0.88, 0.36, 0.74, 0.52, 0.62, 0.44,
];

/**
 * WhatsApp-style voice-note player — play/pause glyph, seekable waveform with
 * a travelling dot, and an m:ss counter (elapsed while playing, total when
 * idle). Mirrors `features/whatsapp/components/AudioPlayer` visually, with
 * brand-green accents and the MediaRecorder-webm duration workaround the
 * internal chat needs. Replaces the raw browser `<audio controls>`.
 */
export function ChatVoiceNote({
  src,
  type,
  isSent,
}: {
  src: string;
  type?: string;
  isSent?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  }, []);

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !duration || !isFinite(duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setCurrent(a.currentTime);
  };

  // MediaRecorder webm blobs report duration = Infinity until you force a seek
  // past the end — the canonical workaround so the counter shows the real length.
  const onLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const a = e.currentTarget;
    if (a.duration === Infinity || Number.isNaN(a.duration)) {
      a.currentTime = 1e101;
      a.addEventListener(
        "timeupdate",
        function once() {
          a.removeEventListener("timeupdate", once);
          a.currentTime = 0;
          setDuration(Number.isFinite(a.duration) ? a.duration : 0);
        },
        { once: true }
      );
    } else {
      setDuration(a.duration);
    }
  };

  const pct =
    duration > 0 && isFinite(duration)
      ? Math.max(0, Math.min(100, (current / duration) * 100))
      : 0;
  const shown = playing || current > 0 ? current : duration;

  // WhatsApp palette, tinted to the app's greens: played bars + dot pick up
  // the brand accent; unplayed bars stay neutral on both bubble colors.
  const glyphColor = isSent ? "#4a7c59" : "#667781";
  const playedColor = isSent ? "#156548" : "#218760";
  const unplayedColor = "rgba(5,25,18,0.18)";

  return (
    <div className="flex w-[240px] max-w-full items-center gap-[7px] py-[3px]">
      {/* Play / pause glyph — borderless, like WhatsApp */}
      <button
        type="button"
        onClick={toggle}
        className="flex h-[34px] w-[34px] shrink-0 items-center justify-center transition-transform active:scale-95"
        style={{ color: glyphColor }}
        aria-label={playing ? "Pause voice note" : "Play voice note"}
      >
        {playing ? (
          <Pause size={22} fill="currentColor" strokeWidth={0} />
        ) : (
          <Play size={22} fill="currentColor" strokeWidth={0} className="ml-[2px]" />
        )}
      </button>

      {/* Waveform + time */}
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div
          onClick={seek}
          className="relative flex h-[26px] cursor-pointer items-center"
        >
          <div className="flex h-full w-full items-center gap-[1.5px]">
            {Array.from({ length: WAVEFORM_BARS }).map((_, i) => {
              const barPct = ((i + 0.5) / WAVEFORM_BARS) * 100;
              const isFilled = barPct <= pct;
              const h = SEED_HEIGHTS[i % SEED_HEIGHTS.length];
              return (
                <div
                  key={i}
                  className="flex-1 rounded-full transition-colors duration-75"
                  style={{
                    height: `${Math.max(14, h * 100)}%`,
                    minHeight: 3,
                    backgroundColor: isFilled ? playedColor : unplayedColor,
                  }}
                />
              );
            })}
          </div>

          {/* travelling seek dot */}
          <div
            className="pointer-events-none absolute top-1/2 h-[11px] w-[11px] -translate-y-1/2 rounded-full shadow-sm transition-[left] duration-75"
            style={{ left: `calc(${pct}% - 5px)`, backgroundColor: playedColor }}
          />
        </div>

        {/* duration + mic row */}
        <div className="flex items-center justify-between px-[1px]">
          <span className="text-[11px] leading-none tabular-nums text-[#667781]">
            {fmtDur(shown)}
          </span>
          <Mic size={11} className="opacity-40" style={{ color: glyphColor }} />
        </div>
      </div>

      <audio
        ref={audioRef}
        preload="metadata"
        onLoadedMetadata={onLoadedMetadata}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
      >
        <source src={src} type={type || "audio/webm"} />
      </audio>
    </div>
  );
}
